import {
	type ChangeEvent,
	type FocusEvent,
	type KeyboardEvent,
	type RefObject,
	useCallback,
	useEffect,
	useRef,
	useState,
} from 'react';

import { buildDate, type ISegment, type ISegmentedDateInputProps, onlyDigits, toSegments } from './DateRangePicker.helpers';

export const SegmentedDateInput = ({ value, syncSignal, ariaLabel, onChange }: ISegmentedDateInputProps) => {
	const [seg, setSeg] = useState<ISegment>(() => toSegments(value));

	const yearRef = useRef<HTMLInputElement>(null);
	const monthRef = useRef<HTMLInputElement>(null);
	const dayRef = useRef<HTMLInputElement>(null);

	useEffect(() => {
		setSeg(toSegments(value));
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [syncSignal]);

	const commit = useCallback(
		(next: ISegment) => {
			setSeg(next);
			onChange(buildDate(next));
		},
		[onChange]
	);

	const handleYear = useCallback(
		(event: ChangeEvent<HTMLInputElement>) => {
			const year = onlyDigits(event.target.value, 4);
			commit({ ...seg, year });
			if (year.length === 4) monthRef.current?.focus();
		},
		[commit, seg]
	);

	const handleMonth = useCallback(
		(event: ChangeEvent<HTMLInputElement>) => {
			let month = onlyDigits(event.target.value, 2);
			if (month.length === 1 && Number(month) > 1) month = `0${month}`;
			if (month.length === 2 && Number(month) > 12) month = '12';
			commit({ ...seg, month });
			if (month.length === 2) dayRef.current?.focus();
		},
		[commit, seg]
	);

	const handleDay = useCallback(
		(event: ChangeEvent<HTMLInputElement>) => {
			let day = onlyDigits(event.target.value, 2);
			if (day.length === 1 && Number(day) > 3) day = `0${day}`;
			if (day.length === 2 && Number(day) > 31) day = '31';
			commit({ ...seg, day });
		},
		[commit, seg]
	);

	const padOnBlur = useCallback(
		(key: 'month' | 'day') => () => {
			const current = seg[key];
			if (current.length === 1) commit({ ...seg, [key]: `0${current}` });
		},
		[commit, seg]
	);

	const focusPrevOnBackspace = useCallback(
		(ref: RefObject<HTMLInputElement | null>) => (event: KeyboardEvent<HTMLInputElement>) => {
			if (event.key === 'Backspace' && event.currentTarget.value === '') ref.current?.focus();
		},
		[]
	);

	const selectOnFocus = useCallback((event: FocusEvent<HTMLInputElement>) => event.target.select(), []);

	return (
		<span
			className='bt_dateRangePicker_segdate'
			role='group'
			aria-label={ariaLabel}
		>
			<input
				ref={yearRef}
				type='text'
				inputMode='numeric'
				className='bt_dateRangePicker_segdate_seg bt_dateRangePicker_segdate_seg--year'
				placeholder='yyyy'
				aria-label={`${ariaLabel} year`}
				maxLength={4}
				value={seg.year}
				onChange={handleYear}
				onFocus={selectOnFocus}
			/>
			<span className='bt_dateRangePicker_segdate_sep'>-</span>
			<input
				ref={monthRef}
				type='text'
				inputMode='numeric'
				className='bt_dateRangePicker_segdate_seg'
				placeholder='mm'
				aria-label={`${ariaLabel} month`}
				maxLength={2}
				value={seg.month}
				onChange={handleMonth}
				onBlur={padOnBlur('month')}
				onKeyDown={focusPrevOnBackspace(yearRef)}
				onFocus={selectOnFocus}
			/>
			<span className='bt_dateRangePicker_segdate_sep'>-</span>
			<input
				ref={dayRef}
				type='text'
				inputMode='numeric'
				className='bt_dateRangePicker_segdate_seg'
				placeholder='dd'
				aria-label={`${ariaLabel} day`}
				maxLength={2}
				value={seg.day}
				onChange={handleDay}
				onBlur={padOnBlur('day')}
				onKeyDown={focusPrevOnBackspace(monthRef)}
				onFocus={selectOnFocus}
			/>
		</span>
	);
};
